/**
 * EditorPlacement: the place-a-prop subsystem (cursor-driven).
 *
 * The editor tracks the mouse cursor over the canvas and computes a world-space
 * ray through it each frame (camera → cursor). The preview marker rides that
 * ray under one of two strategies (selected via `EditorState.placementMode`):
 *   - raycastSurface (default): cast the cursor ray against collision and snap
 *     the preview to the hit surface; falls back to free-depth if no hit / no
 *     collision.
 *   - freeDepth      : float the preview at a wheel-adjustable distance along
 *     the cursor ray.
 *
 * Gesture: pick an asset → move cursor to aim (preview follows) → wheel to tune
 * depth → left-click to commit. Left-drag is reserved by the editor for camera
 * look, so a click (down+up without dragging) is what places. Esc cancels.
 */

import { Entity, Vec3 } from 'playcanvas';
import type { Application } from 'playcanvas';
import type { Collision } from '../collision';
import type { EditorAsset } from './EditorAssetLibrary';
import { createMarkerFromConfig } from '../scene/markerFactory';
import type { EditorState, PlacementMode } from './EditorState';

export class EditorPlacement {
    private _app: Application;
    private _state: EditorState;
    private _getCollision: () => Collision | null;
    private _onConfirm: (asset: EditorAsset, pos: [number, number, number]) => void;

    private _asset: EditorAsset | null = null;
    private _preview: Entity | null = null;
    /** Distance along the cursor ray for freeDepth / fallback (m). */
    private _depth = 8;

    constructor(
        app: Application,
        state: EditorState,
        getCollision: () => Collision | null,
        onConfirm: (asset: EditorAsset, pos: [number, number, number]) => void
    ) {
        this._app = app;
        this._state = state;
        this._getCollision = getCollision;
        this._onConfirm = onConfirm;
    }

    get active(): boolean { return this._asset !== null; }

    begin(asset: EditorAsset): void {
        this.cancel();
        this._asset = asset;
        this._depth = 8;
        this._buildPreview();
    }

    cancel(): void {
        if (this._preview) {
            this._app.root.removeChild(this._preview);
            this._preview.destroy();
            this._preview = null;
        }
        this._asset = null;
    }

    onWheel(deltaY: number): void {
        if (!this._asset) return;
        const step = deltaY > 0 ? 1 : -1;
        this._depth = Math.max(1, Math.min(200, this._depth + step));
    }

    /** Commit the placement at the preview's current position (left-click). */
    commit(): void {
        if (!this._asset) return;
        this._confirm();
    }

    /** Per-frame: keep the preview on the cursor ray per the active strategy.
     *  `dir` must be normalized. */
    update(origin: Vec3, dir: Vec3): void {
        if (!this._asset || !this._preview) return;
        const target = this._resolvePosition(this._state.placementMode, origin, dir);
        this._preview.setPosition(target[0], target[1], target[2]);
    }

    private _resolvePosition(mode: PlacementMode, origin: Vec3, dir: Vec3): [number, number, number] {
        if (mode === 'raycastSurface') {
            const col = this._getCollision();
            if (col) {
                const hit = col.queryRay(origin.x, origin.y, origin.z, dir.x, dir.y, dir.z, 200);
                if (hit) return [hit.x, hit.y, hit.z];
            }
            // No surface hit (or no collision): fall back to free-depth.
            return [origin.x + dir.x * this._depth, origin.y + dir.y * this._depth, origin.z + dir.z * this._depth];
        }
        // freeDepth / numeric: fixed distance along the cursor ray.
        return [origin.x + dir.x * this._depth, origin.y + dir.y * this._depth, origin.z + dir.z * this._depth];
    }

    private _confirm(): void {
        if (!this._asset || !this._preview) { this.cancel(); return; }
        const p = this._preview.getPosition();
        this._onConfirm(this._asset, [p.x, p.y, p.z]);
        this.cancel();
    }

    private _buildPreview(): void {
        if (!this._asset) return;
        const e = createMarkerFromConfig(this._app, this._asset.marker);
        this._app.root.addChild(e);
        this._preview = e;
    }
}
